<template>
  <div class="file-viewer">
    <!-- Top bar -->
    <div class="file-viewer__top-bar">
      <CloseButton white @click="close()" />
      <div
        v-if="selectedFile"
        class="file-viewer__title"
      >
        <Icon
          class="file-viewer__title-icon"
          :color="selectedIconInfo.color"
        >
          {{ selectedIconInfo.icon }}
        </Icon>
        <span class="file-viewer__title-text">{{ selectedFile.name }}</span>
      </div>
      <div class="file-viewer__spacer" />
      <a v-if="selectedFile" :href="selectedFileData" target="_blank" >
        <SBtn v-quick-title="$t('message.DOWNLOAD')" dark icon>
          <Icon image>{{ iconDownloadOutline }}</Icon>
        </SBtn>
      </a>
    </div>
    <!-- END Top bar -->
    <!-- Content -->
    <div class="file-viewer__container">
      <div class="file-viewer__carousel">
        <div class="file-viewer__carousel__wrapper">
          <div
            v-for="(file, fileIdx) in files"
            :key="fileIdx"
            class="file-viewer__carousel-cell"
            :class="{ 'file-viewer__carousel-cell--selected': fileIdx === selectedIndex }"
            @click="onCellClick(fileIdx)"
          >
            <FileThumbnail :file="file" />
          </div>
          <div v-if="editable" class="file-viewer__carousel-cell">
            <img
              class="file-viewer__carousel-image"
              alt="Add"
              src="/images/add-white-big.png"
              @click="addFile()"
            >
          </div>
        </div>
      </div>
      <div class="file-viewer__viewer">
        <ImageEditor
          v-if="editorVisible"
          ref="imageEditor"
          :pathname="pathname"
          :value="selectedFile"
          @save="onSaveEditor"
          @remove="confirmRemoveFile"
          @done="close()"
        />
        <FileView
          v-for="(file, fileIdx) in files"
          v-show="!editorVisible && fileIdx === selectedIndex"
          :key="file.id"
          class="file-viewer__file-view"
          :file="file"
        />
        <div
          v-show="needControls && !editorVisible"
          class="file-viewer__controls"
        >
          <button class="action-btn img-btn red ml50" @click="confirmRemoveFile">
            <img src="/images/delete-white.png" alt="">
          </button>
          <button class="action-btn img-btn green ml10" @click="close()">
            <img src="/images/tick-white.png" alt="">
          </button>
        </div>
      </div>
    </div>
    <!-- END Content -->
    <!-- Hidden components -->
    <FileManager
      v-if="fileManagerVisibility"
      value
      :pathname="pathname"
      @change="onChangeFileManager"
      @close="fileManagerVisibility = false"
    />
    <PictureTaker
      v-if="pictureTakerVisibility"
      @picture="onPictureTaken"
      @close="pictureTakerVisibility = false"
    />
    <input
      v-if="editable"
      v-show="false"
      ref="fileInput"
      type="file"
      :accept="fileTypeAccepted"
      multiple
      @change="onChangeFileInput"
    >
  </div>
</template>

<script>
/**
 * File viewer ; Use to display files and add files to a selection
 *
 * @vue-prop {Boolean} [editable] - Toggle on/off any kind edition (drawing, rotating, saving, etc)
 * @vue-prop {Number} [index=0] - Index of the first file to display (in the given file list)
 * @vue-prop {String} [pathname] - Pathname given to potential uploaded files
 * @vue-prop {Number[]} values - List of file ids
 */
import $ from 'jquery';
import toastr from 'toastr';
// Import components
import CloseButton from '@/components/close-button.vue';
import FileManager from '@/components/file-system/file-manager.vue';
import FileThumbnail from '@/components/file-system/file-thumbnail.vue';
import FileView from '@/components/file-system/file-view.vue';
import Icon from '@/components/icon.vue';
import ImageEditor from '@/components/file-system/image-editor.vue';
import PictureTaker from '@/components/picture-taker.vue';
import SBtn from '@/components/s-btn.vue';
// Import Vuex utils
import { GET_FILE_DETAILS, UPLOAD_FILE, FETCH_FILE_DATA } from '@/store/modules/file/action-types';
// Import utils
import {
  FILE_TYPE_ACCEPTED,
  RE_IMAGE_TYPE,
  getFileIconInfo,
  ACCEPTED_PATHNAMES,
} from '@/utils/file';
import { calert, calertHide, isSmartphone } from '@/utils/main';

const iconDownloadOutline = require('@/assets/img/download-medium-white.png');


export default {
  name: 'FileViewer',

  components: {
    CloseButton,
    FileManager,
    FileThumbnail,
    FileView,
    Icon,
    ImageEditor,
    PictureTaker,
    SBtn,
  },

  props: {
    editable: Boolean,
    index: {
      type: Number,
      default: 0,
    },
    pathname: {
      type: String,
      default: '',
      validator: v => ACCEPTED_PATHNAMES.includes(v),
    },
    value: {
      type: Array,
      default: () => [],
    },
    initWithAdditionalFiles: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      iconDownloadOutline,
      selectedIndex: 0,
      fileManagerVisibility: false,
      pictureTakerVisibility: false,
      isSmartphone: false,
    };
  },

  computed: {
    files() {
      return this.value.map(id => this.$store.state.file.files[id] || { id });
    },
    fileIds() {
      return this.files.map(f => f.id);
    },
    fileTypeAccepted() {
      return Object.values(FILE_TYPE_ACCEPTED).map(v => (v instanceof Array ? v.join(',') : v)).join(',');
    },
    selectedFile() {
      return this.files[this.selectedIndex];
    },
    selectedFileData() {
      return this.selectedFile ? `/server/file/download/${this.selectedFile.id}` : '';
    },
    selectedIconInfo() {
      return getFileIconInfo(this.selectedFile && this.selectedFile.type);
    },
    needControls() {
      return this.editable && this.selectedFile;
    },
    editorVisible() {
      return this.needControls && RE_IMAGE_TYPE.test(this.selectedFile.type);
    },
  },

  watch: {
    index() {
      this.setIndex(this.index);
    },
    value() {
      this.fetchFiles();
    },
    files() {
      this.loadImages();
    },
  },

  created() {
    if (this.initWithAdditionalFiles && this.initWithAdditionalFiles.length) {
      this.uploadFiles(this.initWithAdditionalFiles);
    }
    this.fetchFiles();
    this.setIndex(this.index);
    this.isSmartphone = isSmartphone();
  },

  methods: {
    /** Fetch images data */
    async loadImages() {
      const promises = [];
      this.files.forEach((file) => {
        if (!RE_IMAGE_TYPE.test(file.type)) return;
        promises.push(this.$store.dispatch(`file/${FETCH_FILE_DATA}`, { fileId: file.id }));
      });
      return Promise.all(promises);
    },
    /**
     * Fetch files
     */
    async fetchFiles() {
      return this.$store.dispatch(`file/${GET_FILE_DETAILS}`, { ids: this.value })
        .catch((err) => {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
          throw err;
        });
    },
    /**
     * Set index of the selected file
     */
    setIndex(idx) {
      this.selectedIndex = this.files.length > 0
        ? Math.max(0, idx) % this.files.length
        : 0;
    },
    /**
     * Close the viewer
     * Will save modification and emit changes
     */
    close() {
      const editor = this.$refs.imageEditor;
      if (editor) {
        editor.saveImage()
          .then(() => {
            this.$emit('change', this.fileIds);
            this.$emit('close');
          });
        return;
      }
      this.$emit('change', this.fileIds);
      this.$emit('close');
    },
    /**
     * Upload files on the system ; Will synchronize with the vuex store
     * @param {FileList} files - List of files, probably given by an HTMLInput
     * @return {Promise} Promise, fulfilled when requests are all finished
     */
    async uploadFiles(files) {
      const promises = [];

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        promises.push(
          this.$store.dispatch(`file/${UPLOAD_FILE}`, { file, pathname: this.pathname })
            .catch(() => -1),
        );
      }
      return Promise.all(promises)
        .then((results) => {
          const newIds = results.filter(r => r !== -1).map(f => f.id);
          if (newIds.length > 0) {
            toastr.success(this.$tc('message.FILE_UPLOAD_SUCCESS', newIds.length, { nb: newIds.length }));
            const fileIds = this.fileIds.concat(newIds);
            this.$emit('input', fileIds);
          }
        });
    },
    /** Prompt the user to confirm the file deletion */
    confirmRemoveFile() {
      calert(this.$t('message.CONFIRM_DELETE_FILE'), [
        {
          title: this.$t('message.DELETE'),
          action: () => {
            this.removeFile();
          },
          sClass: 'red',
        },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    /** Remove a file from the current file list and emit the new value */
    removeFile() {
      const fileIds = this.fileIds.slice();
      fileIds.splice(this.selectedIndex, 1);
      this.$emit('input', fileIds);
    },
    /**
     * Trigger the `Select files...` system window (HTMLinput)
     */
    triggerFileInput() {
      $(this.$refs.fileInput).trigger('click');
    },
    /**
     * Trigger a prompt to add a new file to the file list
     */
    addFile() {
      const options = [
        {
          title: this.$t('message.BROWSE_GALLERY'),
          action: this.triggerFileInput,
        },
        {
          title: this.$t('message.TAKE_PICTURE'),
          action: this.openPictureTaker,
        },
        {
          title: this.$t('message.SEARCH_ON_DATABASE'),
          action: this.openFileManager,
        },
      ];
      if (this.isSmartphone) {
        options.splice(1, 1);
        options[0].title = this.$t('message.PICK_PICTURE');
        options[0].hint = this.$t('message.PICK_PICTURE_CAMERA_GALLERY');
      }
      calert(
        this.$t('message.HOW_ADD_FILES'),
        options,
        false,
        '',
        [{ pasteonly: true }],
        () => {
          $(document).off('paste.paste_picture');
        },
      );
      $(document).on('paste.paste_picture', (e) => {
        const { items } = e.clipboardData || e.originalEvent.clipboardData;
        for (const index in items) {
          const item = items[index];
          if (item.kind === 'file') {
            calertHide();
            $(document).off('paste.paste_picture');
            this.uploadFiles([item.getAsFile()]);
          }
        }
      });
    },
    openFileManager() {
      this.fileManagerVisibility = true;
    },
    openPictureTaker() {
      this.pictureTakerVisibility = true;
    },
    /**
     * ==================
     *   EVENT HANDLERS
     * ==================
     */
    onCellClick(idx) {
      const editor = this.$refs.imageEditor;
      if (editor) {
        editor.saveImage()
          .then(() => { this.setIndex(idx); });
        return;
      }
      this.setIndex(idx);
    },
    onChangeFileInput() {
      const { files } = this.$refs.fileInput;
      this.uploadFiles(files);
      this.$refs.fileInput.value = ''; // Clear files
    },
    onSaveEditor({ file, oldFileId }) {
      const fileIds = this.fileIds.slice();
      const idx = fileIds.findIndex(fid => fid === oldFileId);
      if (idx >= 0) {
        fileIds[idx] = file.id;
      } else {
        fileIds.push(file.id);
      }
      this.$emit('input', fileIds);
    },
    onChangeFileManager(files) {
      const newFiles = this.fileIds.slice();
      files.forEach((file) => {
        if (!this.value.includes(file.id)) {
          newFiles.push(file.id);
        }
      });
      this.$emit('input', newFiles);
    },
    onPictureTaken(blob) {
      this.uploadFiles([blob]);
      this.pictureTakerVisibility = false;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.file-viewer {
  position: fixed;
  top: 0px;
  left: $nav-width;
  bottom: 0px;
  right: 0px;
  background: $modal-dark-background;
  box-sizing: border-box;
  overflow: hidden;
  white-space: initial; /* Prevent any page from applying nowrap */
  z-index: 10018;
  align-items: stretch;
  display: flex;
  flex-direction: column;

  *:not(.close-button) {
    box-sizing: border-box;
  }
  &__top-bar {
    position: relative;
    align-items: center;
    border-bottom: 1px solid #dadce0;
    display: flex;
    flex: 0 1 41px;
    max-height: 41px;
    white-space: nowrap;
    padding-right: 3rem;

    &:after {
      display: block;
      content: " ";
      position: absolute;
      right: 2.7rem;
      top: 8px;
      bottom: 8px;
      width: 1px;
      background: white;
      opacity: 0.6;
    }

    .s-btn--icon {
      margin: 2px;
    }

    .close-button {
      top: 3px;
      right: 3px;
    }
  }
  &__spacer {
    flex-grow: 1;
  }
  &__title {
    align-items: center;
    color: #fff;
    display: inline-flex;
    overflow: hidden;
    text-align: center;
    line-height: 40px;
  }
  &__title-icon {
    flex: 0 0 auto;
    margin: 3px 11px;
  }
  &__title-text {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__container {
    align-items: center;
    display: flex;
    flex: 1 1 13%;
    justify-content: center;
    max-height: 100%;
    max-width: 100%;
    overflow: hidden;
  }
  &__carousel {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    padding: 0 5px;
    position: relative;
    width: 13%;

    &__wrapper {
      overflow-x: hidden;
      overflow-y: scroll;
      position: absolute;
      padding-left: 4px;
      padding-right: 24px;
      top: 0px;
      left: 0px;
      right: -20px;
      bottom: 0px;
    }
    &-cell {
      cursor: pointer;
      margin-top: 4px;
      opacity: 0.5;

      &:hover,
      &--selected {
        opacity: 1;
      }
    }
    &-image {
      object-fit: contain;
      width: 100%;
    }
  }
  &__viewer {
    height: 100%;
    flex: 0 1 87%;
  }
  &__controls {
    position: absolute;
    bottom: 30px;
    left: 50%;
    transform: translateX(-50%);
  }
}

@media (max-width: $smartphone-landscape) {
  .file-viewer {
    top: $smartphone-header-height;
    left: $smartphone-nav-width;
  }
}
</style>
